import React from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Componente estilizado para las tarjetas con animaciones
const StyledCard = styled(Card)(({ theme, cardcolor }) => ({
  height: 'auto', // Cambiado de 50% a auto para altura dinámica
  minHeight: '280px', // Altura mínima consistente
  borderRadius: '20px',
  border: `2px solid ${cardcolor}`,
  transition: 'all 0.4s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-15px)',
    boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
    backgroundColor: `${cardcolor}10`,
  },
}));

// Avatar estilizado para los iconos
const StyledAvatar = styled(Avatar)(({ cardcolor }) => ({
  width: 80,
  height: 80,
  background: `linear-gradient(135deg, ${cardcolor}, ${cardcolor}dd)`,
  margin: '0 auto 25px',
  boxShadow: `0 10px 25px ${cardcolor}40`,
  fontSize: '2.2em',
}));

// Contenedor principal con gradiente
const GradientContainer = styled(Box)({
  padding: '100px 0',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  position: 'relative',
  overflow: 'hidden',
});

// Elementos decorativos de fondo
const DecorativeCircle = styled(Box)(({ top, left, bottom, right, size, color }) => ({
  position: 'absolute',
  top,
  left,
  bottom,
  right,
  width: size,
  height: size,
  background: color,
  borderRadius: '50%',
  zIndex: 1,
}));

export const Features = (props) => {
  const colors = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12'];
  
  return (
    <GradientContainer id="features">
      {/* Elementos decorativos de fondo */}
      <DecorativeCircle 
        top="10%" 
        left="-5%" 
        size="200px" 
        color="rgba(52, 152, 219, 0.1)" 
      />
      <DecorativeCircle 
        bottom="10%" 
        right="-5%" 
        size="300px" 
        color="rgba(46, 204, 113, 0.1)" 
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Título de la sección */}
        <Box textAlign="center" mb={10}>
          <Typography 
            variant="h2" 
            component="h2"
            sx={{
              fontSize: '3.5em',
              fontWeight: 'bold',
              color: '#2c3e50',
              mb: 2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Nuestras Características
          </Typography>
          
          <Box
            sx={{
              width: '100px',
              height: '4px',
              background: 'linear-gradient(90deg, #3498db, #2ecc71)',
              margin: '0 auto 30px',
              borderRadius: '2px'
            }}
          />
          
          <Typography 
            variant="h6"
            sx={{
              color: '#7f8c8d',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            Descubre por qué somos líderes en formación vial y seguridad en el transporte
          </Typography>
        </Box>
        
        {/* Grid de características */}
        <Grid container spacing={4} justifyContent="center">
          {props.data
            ? props.data.map((d, i) => {
                const currentColor = colors[i % colors.length];
                
                return (
                  <Grid item xs={12} sm={6} md={3} key={`${d.title}-${i}`}>
                    <StyledCard cardcolor={currentColor}>
                      {/* Círculo decorativo */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '-30px',
                          right: '-30px',
                          width: '80px',
                          height: '80px',
                          background: `${currentColor}10`,
                          borderRadius: '50%',
                          zIndex: 1
                        }}
                      />
                      
                      <CardContent sx={{ textAlign: 'center', p: 4, position: 'relative', zIndex: 2 }}>
                        {/* Icono */}
                        <StyledAvatar cardcolor={currentColor}>
                          <i className={d.icon} />
                        </StyledAvatar>
                        
                        {/* Título */}
                        <Typography 
                          variant="h5" 
                          component="h3"
                          sx={{
                            color: '#2c3e50',
                            fontWeight: 'bold',
                            mb: 2
                          }}
                        >
                          {d.title}
                        </Typography>
                        
                        {/* Descripción */}
                        <Typography 
                          variant="body2"
                          sx={{
                            color: '#7f8c8d',
                            lineHeight: 1.6,
                            mb: 2
                          }}
                        >
                          {d.text}
                        </Typography>
                        
                        {/* Línea decorativa */}
                        <Box
                          sx={{
                            width: '50px',
                            height: '3px',
                            background: currentColor,
                            margin: '0 auto',
                            borderRadius: '2px'
                          }}
                        />
                      </CardContent>
                    </StyledCard>
                  </Grid>
                );
              })
            : (
              <Grid item xs={12}>
                <Box textAlign="center" p={5}>
                  <Typography variant="h6" color="text.secondary">
                    Cargando características...
                  </Typography>
                </Box>
              </Grid>
            )}
        </Grid>
        
        {/* Sección adicional con estadísticas */}
        <Box mt={8}>
          <Paper
            elevation={3}
            sx={{
              background: 'rgba(255,255,255,0.9)',
              borderRadius: '15px',
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="h4" 
              component="h4"
              sx={{
                color: '#2c3e50',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              Confía en Nuestra Experiencia
            </Typography>
            
            <Typography 
              variant="body1"
              sx={{
                color: '#7f8c8d',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: 1.6
              }}
            >
              Desde 2014, hemos sido pioneros en la formación vial integral, 
              combinando tecnología de vanguardia con metodologías probadas para 
              garantizar la mejor preparación de nuestros estudiantes.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </GradientContainer>
  );
};
